// Lightweight client-side ViewState schema validation. The Worker is the hard
// gate; this second pass keeps a malformed cached session from ever reaching
// the renderer.

export const VIEW_SCHEMA_VERSION = 1;
export const ALLOWED_ACTIONS = new Set(['answer', 'replace_view']);
export const ALLOWED_PRESENTATIONS = new Set(['cite', 'quote', 'excerpt', 'card', 'media', 'interactive', 'embed', 'component']);
export const ALLOWED_PROMINENCE = new Set(['feature', 'standard', 'compact']);

const MAX_SECTIONS = 6;
const MAX_BLOCKS = 24;
const MAX_BRIDGE = 850;
const MAX_SELECTED_TEXT = 5200;

export function stripCodeFences(text) {
  let s = String(text || '').trim();
  s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  return s;
}

export function parseJsonPayload(text) {
  if (!text) return null;
  const cleaned = stripCodeFences(text);
  try {
    return JSON.parse(cleaned);
  } catch {
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start < 0 || end <= start) return null;
    try {
      return JSON.parse(cleaned.slice(start, end + 1));
    } catch {
      return null;
    }
  }
}

// Older View workers could emit uncited bridge copy or repeat a source. Keep
// the client boundary conservative by removing only content that cannot be
// rendered with trustworthy provenance. The strict validator still decides
// whether the remaining page is complete enough to show.
export function sanitizeViewResponse(payload, candidates = []) {
  if (!payload || payload.action !== 'replace_view' || !payload.view || typeof payload.view !== 'object') {
    return payload;
  }

  const sourceMap = new Map(
    (candidates || [])
      .filter((candidate) => candidate && typeof candidate.source_id === 'string')
      .map((candidate) => [candidate.source_id, candidate]),
  );
  const knownSources = new Set(sourceMap.keys());
  const usedSources = new Set();
  const sections = (Array.isArray(payload.view.sections) ? payload.view.sections : [])
    .map((section) => {
      if (!section || typeof section !== 'object' || !Array.isArray(section.blocks)) return null;
      const rawBlocks = section.blocks.slice(0, 10);
      rawBlocks.forEach((block, index) => {
        if (!block || block.kind !== 'bridge') return;
        block.text = String(block.text || '')
          .replace(/\s*\[[a-z0-9_-]+:[^\]]+\]/gi, '')
          .replace(/\s*\[(?:\d+(?:\s*,\s*\d+)*)\]/g, '')
          .replace(/\s+([.,!?])/g, '$1')
          .trim();
        const cited = Array.isArray(block.source_ids)
          ? [...new Set(block.source_ids.filter((id) => knownSources.has(id)))]
          : [];
        if (cited.length) {
          block.source_ids = cited;
          return;
        }
        const before = [...rawBlocks.slice(0, index)].reverse()
          .find((candidate) => candidate?.kind === 'source' && knownSources.has(candidate.source_id));
        const after = rawBlocks.slice(index + 1)
          .find((candidate) => candidate?.kind === 'source' && knownSources.has(candidate.source_id));
        block.source_ids = [...new Set([before?.source_id, after?.source_id].filter(Boolean))];
      });
      const blocks = rawBlocks.filter((block) => {
        if (!block || typeof block !== 'object') return false;
        if (block.kind === 'bridge') {
          return Array.isArray(block.source_ids)
            && block.source_ids.length > 0
            && block.source_ids.every((id) => knownSources.has(id));
        }
        if (block.kind !== 'source' || !knownSources.has(block.source_id) || usedSources.has(block.source_id)) {
          return false;
        }
        const source = sourceMap.get(block.source_id);
        if (source?.presentations?.length === 1 && source.presentations[0] === 'component') {
          block.presentation = 'component';
        }
        usedSources.add(block.source_id);
        return true;
      });
      return blocks.length ? { ...section, blocks } : null;
    })
    .filter(Boolean);

  return {
    ...payload,
    view: {
      ...payload.view,
      sections,
    },
  };
}

function bad(message) {
  return { ok: false, error: message, view: null };
}

export function validateViewResponse(payload, candidates = []) {
  if (!payload || typeof payload !== 'object') return bad('Response is not an object.');
  if (!ALLOWED_ACTIONS.has(payload.action)) return bad('Unknown action.');

  const candidateMap = new Map();
  for (const c of candidates || []) {
    if (c && typeof c.source_id === 'string') candidateMap.set(c.source_id, c);
  }

  if (payload.action === 'answer') {
    const message = String(payload.message || '').slice(0, 1000);
    const sourceIds = Array.isArray(payload.source_ids) ? payload.source_ids.slice(0, 80) : [];
    if (!message) return bad('Answer message is empty.');
    if (!sourceIds.every((id) => candidateMap.has(id))) return bad('Answer cites an unknown source.');
    return { ok: true, error: null, view: null, answer: { action: 'answer', message, source_ids: sourceIds } };
  }

  const view = payload.view;
  if (!view || typeof view !== 'object') return bad('replace_view is missing a view.');
  if (!view.closing || typeof view.closing.text !== 'string' || !view.closing.text.trim()) return bad('replace_view is missing its closing.');
  if (!Array.isArray(view.sections) || view.sections.some((section) => typeof section?.exhibition_intro !== 'string' || !section.exhibition_intro.trim())) {
    return bad('replace_view is missing an exhibition introduction.');
  }
  const errors = validateViewState(view, candidateMap);
  if (errors) return bad(errors);
  return { ok: true, error: null, view, answer: null };
}

export function validateViewState(view, candidateMap = new Map()) {
  if (!view || typeof view !== 'object') return 'ViewState is not an object.';
  if (view.schema_version !== VIEW_SCHEMA_VERSION) return 'Unsupported schema_version.';
  if (view.question != null && (typeof view.question !== 'string' || view.question.length > 800)) return 'question is invalid.';
  if (typeof view.lens !== 'string') return 'lens must be a string.';
  if (view.lens.length > 900) return 'lens is too long.';
  if (typeof view.title !== 'string' || view.title.length > 180) return 'title is invalid.';
  if (typeof view.dek !== 'string' || view.dek.length > 500) return 'dek is invalid.';
  if (view.illustration != null) {
    if (!view.illustration || typeof view.illustration !== 'object') return 'illustration is invalid.';
    if (typeof view.illustration.src !== 'string' || !/^https:\/\//i.test(view.illustration.src) || view.illustration.src.length > 1200) return 'illustration source is invalid.';
    if (typeof view.illustration.alt !== 'string' || view.illustration.alt.length > 300) return 'illustration alt text is invalid.';
  }
  if (!Array.isArray(view.sections) || !view.sections.length || view.sections.length > MAX_SECTIONS) return 'sections is invalid or too long.';

  let totalBlocks = 0;
  let sourceBlocks = 0;
  let selectedText = 0;
  const usedSources = new Set();
  for (const section of view.sections) {
    if (!section || typeof section !== 'object') return 'Invalid section.';
    if (typeof section.heading !== 'string' || section.heading.length > 180) return 'Invalid section heading.';
    if (!Array.isArray(section.blocks) || !section.blocks.length || section.blocks.length > 10) return 'Invalid section blocks.';
    if (section.blocks[0]?.kind !== 'bridge') return 'Each section must begin with a narrative bridge.';
    for (let blockIndex = 0; blockIndex < section.blocks.length; blockIndex += 1) {
      const block = section.blocks[blockIndex];
      totalBlocks += 1;
      if (totalBlocks > MAX_BLOCKS) return 'Too many blocks.';
      if (!block || typeof block !== 'object') return 'Invalid block.';
      if (block.kind === 'bridge') {
        if (typeof block.text !== 'string' || !block.text.trim() || block.text.length > MAX_BRIDGE) return 'Invalid bridge.';
        if (!Array.isArray(block.source_ids) || !block.source_ids.length) return 'Bridge source_ids must be a non-empty array.';
        if (!block.source_ids.every((id) => candidateMap.has(id))) return 'Bridge cites an unknown source.';
        continue;
      }
      if (block.kind !== 'source') return 'Unknown block kind.';
      const source = candidateMap.get(block.source_id);
      if (!source) return `Unknown source_id: ${block.source_id}`;
      const previousBlock = section.blocks[blockIndex - 1];
      const followsRegisteredParent = previousBlock?.kind === 'source'
        && source.companion_for === previousBlock.source_id;
      if (previousBlock?.kind !== 'bridge' && !followsRegisteredParent) {
        return 'Every source block must immediately follow a narrative bridge.';
      }
      if (usedSources.has(block.source_id)) return `Duplicate source_id: ${block.source_id}`;
      usedSources.add(block.source_id);
      sourceBlocks += 1;
      if (!ALLOWED_PRESENTATIONS.has(block.presentation)) return 'Invalid presentation.';
      if (!(source.presentations || []).includes(block.presentation)) {
        return `Presentation not allowed for ${block.source_id}.`;
      }
      if (!ALLOWED_PROMINENCE.has(block.prominence)) return 'Invalid prominence.';
      if (!Array.isArray(block.part_indices)) return 'part_indices must be an array.';
      if (['cite', 'quote', 'excerpt'].includes(block.presentation) && !block.part_indices.length) {
        return `part_indices is required for ${block.presentation}.`;
      }
      const partsLen = Array.isArray(source.parts) ? source.parts.length : 0;
      if (block.part_indices.some((i) => !Number.isInteger(i) || i < 0 || i >= partsLen)) {
        return `part_indices out of range for ${block.source_id}.`;
      }
      selectedText += block.part_indices.reduce((sum, i) => sum + String(source.parts[i] || '').length, 0);
      if (selectedText > MAX_SELECTED_TEXT) return 'Selected source text is too long.';
    }
  }
  if (sourceBlocks < 1) return 'A composed view requires at least one source block.';
  return null;
}
